import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { translateZoneName, clearTranslationCache } from '@medical/shared/utils/translateZoneName';
import EnhancedBodyDiagram from '../components/EnhancedBodyDiagram';
import BodyViewSelector from '../components/BodyViewSelector';
import PainScaleModal from '../components/PainScaleModal';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import AudioPlayer from '../components/AudioPlayer';
import toast from 'react-hot-toast';
import { startOfDay, endOfDay } from 'date-fns';
import { useOfflineSync } from '../contexts/OfflineSyncContext';

const BodyDiagramContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;

  @media (max-width: 768px) {
    padding: 1rem 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin: 0 0 0.25rem 0;
  }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.875rem;
  }
`;

const Instructions = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-bottom: 1rem;
    border-radius: 0.5rem;
  }
`;

const InstructionsTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InstructionsList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
  color: #6b7280;
  line-height: 1.6;
`;

const InstructionsItem = styled.li`
  margin-bottom: 0.5rem;
`;

const PainDataContainer = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
    border-radius: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-bottom: 1rem;
    border-radius: 0.5rem;
  }
`;

const PainDataHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const PainDataTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const ClearButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #dc2626;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f87171;
  }
`;

const PainDataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const PainDataItem = styled.div`
  background: ${props => (props.pending ? '#fffbeb' : '#f8fafc')};
  border: ${props => (props.pending ? '1px dashed #f59e0b' : '1px solid #e2e8f0')};
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  opacity: ${props => (props.pending ? 0.95 : 1)};

  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.5rem;
  }
`;

const PainIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => {
    // Handle hex colors directly
    if (props.color && props.color.startsWith('#')) {
      return props.color;
    }
    // Handle color names
    switch (props.color) {
      case 'red': return '#ef4444';
      case 'yellow': return '#f59e0b';
      case 'green': return '#10b981';
      case 'blue': return '#60a5fa';
      default: return '#e5e7eb';
    }
  }};
  flex-shrink: 0;
`;

const PainInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PainInfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const PainZone = styled.div`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const PainLevel = styled.div`
  font-size: 0.75rem;
  color: ${props => {
    // If painColor is provided and valid, use it (prioritize stored color)
    if (props.painColor && props.painColor.startsWith('#')) {
      return props.painColor;
    }
    // Otherwise, map from painLevel
    switch (props.painLevel) {
      case 'worst-pain': return '#ef4444';
      case 'severe': return '#f97316';
      case 'moderate': return '#f59e0b';
      case 'mild': return '#60a5fa';
      case 'no-pain': return '#10b981';
      default: return '#6b7280';
    }
  }};
  text-transform: capitalize;
  font-weight: 500;
`;

const PainTime = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 480px) {
    width: 100%;
    justify-content: stretch;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid
    ${props =>
      props.status === 'online'
        ? '#10b981'
        : props.status === 'syncing'
        ? '#3b82f6'
        : '#f59e0b'};
  background:
    ${props =>
      props.status === 'online'
        ? 'rgba(16, 185, 129, 0.12)'
        : props.status === 'syncing'
        ? 'rgba(59, 130, 246, 0.12)'
        : 'rgba(245, 158, 11, 0.12)'};
  color:
    ${props =>
      props.status === 'online'
        ? '#047857'
        : props.status === 'syncing'
        ? '#1d4ed8'
        : '#92400e'};
`;

const PendingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #fef3c7;
  border: 1px solid #facc15;
  color: #92400e;
  font-size: 0.75rem;
  font-weight: 600;
`;

const SyncButton = styled.button`
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.45rem 0.9rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PendingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: #fde68a;
  color: #92400e;
  font-size: 0.7rem;
  font-weight: 600;
`;

const BodyDiagram = () => {
  const { t, i18n } = useTranslation();
  const [painData, setPainData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('front');
  const [selectedZone, setSelectedZone] = useState(null);
  const [showPainModal, setShowPainModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render on language change

  // Listen to language changes and refresh
  useEffect(() => {
    const handleLanguageChange = () => {
      clearTranslationCache(); // Clear cache when language changes
      setRefreshKey(prev => prev + 1); // Force component re-render
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const {
    isOnline,
    syncing,
    pendingPainLogs,
    triggerSync,
    queuePainLog,
    lastSync
  } = useOfflineSync();

  const fetchPainData = useCallback(async () => {
    if (!isOnline) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const today = new Date();
      const startOfToday = startOfDay(today);
      const endOfToday = endOfDay(today);

      const response = await axios.get('/api/pain-logs', {
        params: {
          startDate: startOfToday.toISOString(),
          endDate: endOfToday.toISOString()
        }
      });

      setPainData(response.data.painLogs || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching pain data:', err);
      setError(t('bodyDiagram.errors.loadFailed', { defaultValue: 'Failed to load pain data' }));
      toast.error(t('bodyDiagram.errors.loadFailed', { defaultValue: 'Failed to load pain data' }));
    } finally {
      setLoading(false);
    }
  }, [isOnline]);

  useEffect(() => {
    fetchPainData();
  }, [fetchPainData, lastSync]);

  const handleZoneClick = (logData) => {
    setSelectedZone(logData);
    setShowPainModal(true);
  };

  const handlePainLog = async (painLevel, painColor, additionalData) => {
    if (!selectedZone) return;

    if (additionalData && additionalData.clear) {
      setPainData(prevData => prevData.filter(log => log.zoneId !== selectedZone.zoneId));
      setShowPainModal(false);
      setSelectedZone(null);
      return;
    }

    const correctView = selectedZone.zoneId.startsWith('back-') ? 'back' : 'front';
    const timestamp = new Date().toISOString();

    const payload = {
      zoneId: selectedZone.zoneId,
      zoneName: selectedZone.zoneName,
      painLevel,
      painColor,
      view: correctView,
      timestamp
    };

    const appendOptionalValue = (key, value) => {
      if (value === undefined || value === null) return;
    if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'string' && !value.trim()) return;
      payload[key] = value;
    };

    appendOptionalValue('painIntensity', additionalData?.painIntensity);
    appendOptionalValue('notes', additionalData?.notes?.trim());
    appendOptionalValue('symptoms', additionalData?.symptoms);
    appendOptionalValue('triggers', additionalData?.triggers);
    appendOptionalValue('timeOfDay', additionalData?.timeOfDay);
    appendOptionalValue('mood', additionalData?.mood);
    appendOptionalValue('activity', additionalData?.activity);
    appendOptionalValue('duration', additionalData?.duration);
    appendOptionalValue('medication', additionalData?.medication);
    appendOptionalValue('distressScore', additionalData?.distressScore);
    appendOptionalValue('distressNotes', additionalData?.distressNotes?.trim());
    appendOptionalValue('distressSources', additionalData?.distressSources);
    if (additionalData?.recordedAfterMedication) {
      payload.recordedAfterMedication = true;
      payload.medicationTakenAt = additionalData.medicationTakenAt;
      payload.postMedicationMedicationName = additionalData.postMedicationMedicationName;
    }

    const formData = new FormData();
    formData.append('zoneId', payload.zoneId);
    formData.append('zoneName', payload.zoneName);
    formData.append('painLevel', payload.painLevel);
    formData.append('painColor', payload.painColor);
    formData.append('view', payload.view);

    Object.entries(payload).forEach(([key, value]) => {
      if (['zoneId', 'zoneName', 'painLevel', 'painColor', 'view', 'timestamp'].includes(key)) {
        return;
      }
      if (key === 'recordedAfterMedication') {
        formData.append(key, value ? 'true' : 'false');
        return;
      }
      if (Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
      } else if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    if (additionalData?.audioBlob) {
      formData.append('audioNote', additionalData.audioBlob, 'audio-recording.webm');
    }

    const queueAndClose = () => {
      queuePainLog(payload, {
        zoneName: selectedZone.zoneName,
        painLevel
      });
      setShowPainModal(false);
      setSelectedZone(null);
    };

    if (!isOnline) {
      queueAndClose();
      return;
    }

    try {
      const response = await axios.post('/api/pain-logs', formData);

      setPainData(prevData => {
        const filtered = prevData.filter(log => log.zoneId !== selectedZone.zoneId);
        return [...filtered, response.data.painLog];
      });

      setShowPainModal(false);
      setSelectedZone(null);
      toast.success(
        additionalData?.audioBlob 
          ? t('bodyDiagram.success.withAudio')
          : t('bodyDiagram.success.recorded')
      );
    } catch (err) {
      console.error('Error logging pain:', err);
      console.error('Error details:', err.response?.data);

      if (!err.response || err.code === 'ERR_NETWORK') {
        queueAndClose();
        return;
      }

      toast.error(t('bodyDiagram.errors.failed', { message: err.response?.data?.message || err.message }));
    }
  };

  const clearAllData = async () => {
    if (!window.confirm(t('bodyDiagram.confirm.clearAll'))) {
      return;
    }
    
    try {
      const today = new Date();
      const startOfToday = startOfDay(today);
      const endOfToday = endOfDay(today);
      
      // Get all today's logs
      const response = await axios.get('/api/pain-logs', {
        params: {
          startDate: startOfToday.toISOString(),
          endDate: endOfToday.toISOString()
        }
      });
      
      const deletePromises = (response.data.painLogs || []).map(log =>
        axios.delete(`/api/pain-logs/${log._id}`)
      );
      
      await Promise.all(deletePromises);
      await fetchPainData();
      toast.success(t('bodyDiagram.success.cleared', { defaultValue: 'All pain data cleared successfully!' }));
    } catch (err) {
      console.error('Error clearing pain data:', err);
      toast.error(t('bodyDiagram.errors.clearFailed', { defaultValue: 'Failed to clear pain data' }));
    }
  };

  const combinedPainData = useMemo(() => {
    const pendingEntries = (pendingPainLogs || []).map(entry => ({
      ...entry,
      id: entry.localId,
      timestamp: entry.timestamp || entry.createdAt,
      isPending: true
    }));

    const liveEntries = (painData || []).map(log => ({
      ...log,
      id: log.id || log._id,
      isPending: false
    }));

    return [...pendingEntries, ...liveEntries].sort((a, b) => {
      const timeA = new Date(a.timestamp || a.createdAt || 0).getTime();
      const timeB = new Date(b.timestamp || b.createdAt || 0).getTime();
      return timeB - timeA;
    });
  }, [painData, pendingPainLogs]);

  const formatTime = (timestamp) => {
    try {
      if (!timestamp) return t('bodyDiagram.time.unknown', { defaultValue: 'Unknown time' });
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return t('bodyDiagram.time.invalidDate', { defaultValue: 'Invalid date' });
      
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      
      // Show precise time for recent entries (within 24 hours)
      const timeString = date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      
      if (diffInMinutes < 1) return `${timeString} (${t('bodyDiagram.time.justNow', { defaultValue: 'Just now' })})`;
      if (diffInMinutes < 60) return `${timeString} (${diffInMinutes}${t('bodyDiagram.time.minutesAgo', { defaultValue: 'm ago' })})`;
      if (diffInMinutes < 1440) return `${timeString} (${Math.floor(diffInMinutes / 60)}${t('bodyDiagram.time.hoursAgo', { defaultValue: 'h ago' })})`;
      
      // Show full date and time for older entries
      return `${date.toLocaleDateString()} ${timeString}`;
    } catch (e) {
      return t('bodyDiagram.time.invalid', { defaultValue: 'Invalid time' });
    }
  };

  const getPainLevelText = (level) => {
    if (!level || typeof level !== 'string') return t('dashboard.stats.noPain');
    switch (level) {
      case 'worst-pain': return t('dashboard.stats.worstPain');
      case 'severe': return t('dashboard.stats.severePain');
      case 'moderate': return t('dashboard.stats.moderatePain');
      case 'mild': return t('dashboard.stats.mild');
      case 'no-pain': return t('dashboard.stats.noPain');
      default: return level;
    }
  };

  const getPainColor = (painLevel, painColor) => {
    // If painColor is provided and valid, use it
    if (painColor && (painColor.startsWith('#') || ['red', 'yellow', 'green', 'blue'].includes(painColor))) {
      return painColor;
    }
    // Otherwise, map from painLevel
    switch (painLevel) {
      case 'worst-pain': return '#ef4444';
      case 'severe': return '#f97316';
      case 'moderate': return '#f59e0b';
      case 'mild': return '#60a5fa';
      case 'no-pain': return '#10b981';
      default: return '#e5e7eb';
    }
  };

  if (loading) {
    return <LoadingSpinner text={t('bodyDiagram.loading', { defaultValue: 'Loading body diagram...' })} />;
  }

  if (error) {
    return (
      <BodyDiagramContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>{t('bodyDiagram.errors.title', { defaultValue: 'Error loading body diagram' })}</h2>
          <p>{error}</p>
          <button onClick={fetchPainData} className="btn btn-primary">
            {t('dashboard.actions.retry')}
          </button>
        </div>
      </BodyDiagramContainer>
    );
  }

  // BULLETPROOF VERSION 3.0 - Group pain data by zone and ensure safe access
  const painDataArray = Array.isArray(combinedPainData) 
    ? combinedPainData.reduce((acc, log) => {
        if (!log || !log.zoneId) return acc;
        
        const existingZone = acc.find(item => item.zoneId === log.zoneId);
        if (existingZone) {
          existingZone.logs.push(log);
        } else {
          acc.push({
            zoneId: log.zoneId,
            zoneName: log.zoneName || 'Unknown Zone',
            logs: [log]
          });
        }
        return acc;
      }, [])
    : [];

  return (
    <BodyDiagramContainer>
      <Header>
        <Title>{t('bodyDiagram.title')}</Title>
        <Subtitle>{t('bodyDiagram.subtitle')}</Subtitle>
      </Header>

      <Instructions>
        <InstructionsTitle>
          📋 {t('bodyDiagram.instructions.title')}
        </InstructionsTitle>
        <InstructionsList>
          <InstructionsItem>
            {t('bodyDiagram.instructions.step1')}
          </InstructionsItem>
          <InstructionsItem>
            {t('bodyDiagram.instructions.step2')}
          </InstructionsItem>
          <InstructionsItem>
            {t('bodyDiagram.instructions.step3')}
          </InstructionsItem>
          <InstructionsItem>
            {t('bodyDiagram.instructions.step4')}
          </InstructionsItem>
        </InstructionsList>
      </Instructions>

      <BodyViewSelector 
        currentView={currentView}
        onViewChange={setCurrentView}
      />
      
      <EnhancedBodyDiagram
        currentView={currentView}
        onPainLog={handleZoneClick}
        painData={combinedPainData}
        externalSelectedZone={selectedZone}
      />

      {painDataArray.length > 0 && (
        <PainDataContainer>
          <PainDataHeader>
            <PainDataTitle>{t('bodyDiagram.todaysRecords', { defaultValue: "Today's Pain Records" })}</PainDataTitle>
            <HeaderActions>
              <StatusPill status={!isOnline ? 'offline' : syncing ? 'syncing' : 'online'}>
                {!isOnline ? t('bodyDiagram.status.offline') : syncing ? t('bodyDiagram.status.syncing') : t('bodyDiagram.status.online')}
              </StatusPill>
              {pendingPainLogs.length > 0 && (
                <PendingBadge title={t('bodyDiagram.pending', { defaultValue: 'Pending Sync' })}>
                  ⏳ {pendingPainLogs.length} {t('bodyDiagram.pending', { defaultValue: 'pending' })}
                </PendingBadge>
              )}
              {isOnline && pendingPainLogs.length > 0 && (
                <SyncButton onClick={triggerSync} disabled={syncing}>
                  {syncing ? t('bodyDiagram.actions.syncing') : t('bodyDiagram.actions.sync')}
                </SyncButton>
              )}
              <ClearButton onClick={clearAllData} disabled={!isOnline}>
                {t('bodyDiagram.actions.clearAll')}
              </ClearButton>
            </HeaderActions>
          </PainDataHeader>

          <PainDataGrid>
            {painDataArray.map(({ zoneId, zoneName, logs }) => {
              const latestLog = Array.isArray(logs) && logs.length > 0 ? logs[0] : null;
              return (
                <PainDataItem key={zoneId} pending={latestLog?.isPending}>
                  <PainIndicator color={getPainColor(latestLog?.painLevel, latestLog?.painColor)} />
                  <PainInfo>
                    <PainInfoText>
                      <PainZone>{translateZoneName(zoneName, t) || t('bodyDiagram.unknownZone', { defaultValue: 'Unknown Zone' })}</PainZone>
                      <PainLevel painLevel={latestLog?.painLevel || 'no-pain'} painColor={latestLog?.painColor}>
                      {typeof latestLog?.painIntensity === 'number'
                        ? `VAS ${latestLog.painIntensity} (${getPainLevelText(latestLog?.painLevel || 'no-pain')})`
                        : getPainLevelText(latestLog?.painLevel || 'no-pain')}
                    </PainLevel>
                      <PainTime>
                        {formatTime(
                          latestLog?.timestamp ||
                            latestLog?.createdAt ||
                            new Date().toISOString()
                        )}
                      </PainTime>
                    </PainInfoText>
                    {latestLog?.isPending && <PendingLabel>{t('bodyDiagram.pending', { defaultValue: 'Pending sync' })}</PendingLabel>}
                    {latestLog?.audioNote && latestLog.audioNote.filename && (
                      <AudioPlayer audioNote={latestLog.audioNote} />
                    )}
                  </PainInfo>
                </PainDataItem>
              );
            })}
          </PainDataGrid>
        </PainDataContainer>
      )}

      {painDataArray.length === 0 && (
        <EmptyState>
          <EmptyIcon>🎯</EmptyIcon>
          <EmptyTitle>{t('bodyDiagram.empty.title')}</EmptyTitle>
          <EmptyText>{t('bodyDiagram.empty.text')}</EmptyText>
        </EmptyState>
      )}

      {showPainModal && selectedZone && (
        <PainScaleModal
          zoneName={translateZoneName(selectedZone.zoneName, t)}
          onSelect={handlePainLog}
          onClose={() => {
            setShowPainModal(false);
            setSelectedZone(null);
          }}
          view={currentView}
        />
      )}
    </BodyDiagramContainer>
  );
};

export default BodyDiagram;
